import { tryCatch } from '@gozoki/tools'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormErrorAlert } from '~/components/new/app-ui/form-error-alert'
import { AuthCard } from '~/components/routes/login/auth-card'
import {
  type SessionStart,
  sessionStartOf,
  UNKNOWN_ERROR_MESSAGE,
} from '~/components/routes/login/login-session'
import { MfaLoginStep } from '~/components/routes/login/mfa-login-step'
import { PasswordResetStep } from '~/components/routes/login/password-reset-step'
import { codeRequestOpensEntry, zResetEmail } from '~/components/routes/login/password-reset'
import { Button } from '~/components/ui/button'
import { useAppForm } from '~/hooks/form-hooks'
import { api } from '~/lib/tuyau'
import { getApiErrorMessage, getTuyauErrorStatus } from '~/lib/utils'
import { signIn } from '~/utils/session-navigation'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

const zLogin = z.object({
  email: zResetEmail,
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
})

function LoginPage() {
  // Adresse de l'étape de saisie du code de réinitialisation, ouverte après une demande de code ou
  // par « J'ai déjà un code ».
  const [resetEmail, setResetEmail] = useState<string | null>(null)
  // Défi du double facteur d'un admin enrôlé (S1.4b) : l'étape « code » remplace le formulaire.
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)

  const requestCode = useMutation(api.auth.askResetPasswordAdmin.mutationOptions())
  const { mutateAsync: login } = useMutation(api.auth.loginAdmin.mutationOptions())

  /** Ouvre la session avec le jeton d'une connexion : mot de passe, réinitialisation ou code. */
  const openSession = async (token: string) => {
    try {
      await signIn(token)
    } catch (error) {
      setResetEmail(null)
      setChallengeToken(null)
      setLoginError(getApiErrorMessage(error, UNKNOWN_ERROR_MESSAGE))
    }
  }

  /** Identité vérifiée : session ouverte, ou étape « code » pour un admin enrôlé. */
  const startSession = async (start: SessionStart) => {
    if ('token' in start) return openSession(start.token)
    setResetEmail(null)
    setChallengeToken(start.challengeToken)
  }

  const form = useAppForm({
    defaultValues: { email: '', password: '' },
    validators: { onSubmit: zLogin },
    onSubmit: async ({ value }) => {
      setLoginError(null)
      const [start, error] = await tryCatch(
        login({ body: { email: value.email.trim(), password: value.password } }).then(
          sessionStartOf,
        ),
      )
      if (start === null) {
        setLoginError(getApiErrorMessage(error, UNKNOWN_ERROR_MESSAGE))
        return
      }
      await startSession(start)
    },
  })

  const askResetCode = () => {
    const email = form.getFieldValue('email').trim()
    if (!zResetEmail.safeParse(email).success) {
      toast.error(`L'email "${email}" n'est pas valide. Entrez votre email`)
      return
    }

    requestCode.mutate(
      { body: { email } },
      {
        onSuccess: (response) => {
          // Le type inclut `undefined` (branche du limiteur, qui répond sans corps typé).
          if (response) toast.success(response.message)
          setResetEmail(email)
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error, UNKNOWN_ERROR_MESSAGE))
          // Trop de demandes : le dernier code envoyé reste valable, la saisie s'ouvre quand même.
          if (codeRequestOpensEntry(getTuyauErrorStatus(error))) setResetEmail(email)
        },
      },
    )
  }

  if (challengeToken !== null) {
    return (
      <MfaLoginStep
        challengeToken={challengeToken}
        onBack={() => setChallengeToken(null)}
        onToken={openSession}
      />
    )
  }

  if (resetEmail !== null) {
    return (
      <PasswordResetStep
        email={resetEmail}
        onBack={() => setResetEmail(null)}
        onPasswordReset={startSession}
      />
    )
  }

  return (
    <AuthCard title="Back Office">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void form.handleSubmit()
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <form.AppField name="email">
          {(field) => <field.TextField label="Email" type="email" autoComplete="username" />}
        </form.AppField>
        <form.AppField name="password">
          {(field) => (
            <field.TextField label="Mot de passe" type="password" autoComplete="current-password" />
          )}
        </form.AppField>

        <FormErrorAlert message={loginError} />

        <form.AppForm>
          <form.SubmitButton className="w-full">Se connecter</form.SubmitButton>
        </form.AppForm>

        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={askResetCode}
            disabled={requestCode.isPending}
          >
            Mot de passe oublié ?
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => setResetEmail(form.getFieldValue('email').trim())}
          >
            J'ai déjà un code
          </Button>
        </div>
      </form>
    </AuthCard>
  )
}
